import { computed, inject, Injectable, Injector, Signal, signal } from '@angular/core';
import {
  CompleteProgressStepsDesktop,
  CompleteProgressStepsMobile,
  ProgressStepNames,
  QUERY_PARAMS,
  ROUTER_PATHS
} from '../../app.const';
import { BreakpointObserverService, NavigationService } from '@pc/shared';
import {
  fragmentsProgressMap,
  newUserFragmentsProgressMap,
  newUserStepProgressMap,
  stepProgressMap
} from '../../pages/trip-details/models/step-progress-map';
import {
  coversFragmentsProgressMap,
  coversStepProgressMap
} from '../../pages/covers-page/models/covers-progress-step-map';
import {
  travellerFragmentsProgressMap,
  travellerStepProgressMap
} from '../../pages/traveller-details/models/traveller-progress-step-map';
import {
  checkoutFragmentsProgressMap,
  checkoutStepProgressMap
} from '../../pages/secure-checkout/models/checkout-progress-step-map';
import { Observable, tap } from 'rxjs';
import { ProgressStepperService } from '@pc/progress-stepper';
import { MainStore } from '../../store/main-store/main-store';
import { NewUserTripStep, TripStep } from '../../pages/trip-details/models/trip-step.enum';
import { TravelerSteps } from '../../pages/traveller-details/models/traveller-steps';
import { CheckoutSteps } from '../../pages/secure-checkout/models/checkout-steps';
import { IProgressStep, ProgressValues } from '@pc/progress-step-indicator';
import { CoversSteps } from '../../pages/covers-page/models/covers-steps';
import { ProcessTypeStore } from '../../store/process-type-store/process-type-store';
import { UrlVarsKey } from '../models/agent/url-vars.model';
import { ActivatedRoute } from '@angular/router';

export type SubStepsType = NewUserTripStep | TripStep | CoversSteps | TravelerSteps | CheckoutSteps;

export enum AppSteps {
  TripDetails = 0,
  CoverageSelection = 1,
  TravelersDetails = 2,
  SecureCheckout = 3
}

@Injectable({
  providedIn: 'root'
})
export class StepsManagementService {
  public currentStep = computed(() => this.progressStepperService.currentStep().name);
  public currentSubStep = computed(() => this.stepsState()[this.currentPageIndex()]);
  public isStepCompleted = computed(() => this.steps()[this.currentPageIndex()].isCompleted);
  public isLoading = computed(() => this.mainStore.isLoading());
  public isGlobalLoading = computed(() => this.processTypeStore.isLoading());
  public isBrowserNavigation = true;

  private injector = inject(Injector);
  private breakpoint = inject(BreakpointObserverService);
  private isDesktop: Signal<boolean>;
  private route = inject(ActivatedRoute);

  private navigationService = inject(NavigationService);
  private progressStepperService = inject(ProgressStepperService);
  private mainStore = inject(MainStore);
  private processTypeStore = inject(ProcessTypeStore);
  private isNewUser = this.mainStore.isNewUser();

  private useGateway = computed(() => this.mainStore.useGateway());
  private steps = signal<IProgressStep[]>([]);
  private stepsState = signal<number[]>([]);
  private currentPageIndex = this.progressStepperService.selectedIndex;
  private globalLoader = signal(false);
  private isProcessCompleted = signal(false);

  private currentUrl = '';
  private previousUrl = '';

  private actionOnSubmit?: () => Observable<unknown> = undefined;

  constructor() {
    this.isDesktop = this.breakpoint.getMediumBreakpoint(this.injector);

    const progressSteps = this.isDesktop() ? CompleteProgressStepsDesktop : CompleteProgressStepsMobile;

    this.steps.set(progressSteps);
    this.stepsState.set(new Array(progressSteps.length).fill(0));
  }

  public stepInitializer(
    actionOnSubmit?: () => Observable<unknown>,
    forceSubStep?: SubStepsType,
    navigate = true
  ): void {
    this.mainStore.setIsLoading(false);
    this.actionOnSubmit = actionOnSubmit;

    this.previousUrl = this.currentUrl;
    this.currentUrl = window.location.pathname;

    if (navigate) {
      this.navigateToStep(this.currentPageIndex(), forceSubStep);
    }
  }

  public completeProcess(): void {
    this.isProcessCompleted.set(true);
    this.navigationService
      .navigate([ROUTER_PATHS.SUCCESS_PAGE], { replaceUrl: true, queryParams: this.filteredQueryParams() }, true)
      .pipe(
        tap(() => {
          this.progressStepperService.updateProgress(ProgressValues.OneHundred);
        })
      )
      .subscribe();
  }

  public showGateway(): void {
    this.isBrowserNavigation = false;
    this.navigationService
      .navigate([ROUTER_PATHS.GATEWAY], { replaceUrl: false }, true)
      .pipe(
        tap(() => {
          this.progressStepperService.updateProgress(ProgressValues.OneHundred);
          this.mainStore.setIsLoading(false);
        })
      )
      .subscribe();
  }

  public nextStep(): void {
    if (this.isStepCompleted()) {
      this.setNewStep();
    } else {
      this.setSubStep();
    }
  }

  public previousStep(): void {
    if (this.currentUrl.includes(ROUTER_PATHS.GATEWAY)) {
      return this.navigateToStep(AppSteps.TripDetails);
    }

    this.progressStepperService.previousStep();
  }

  public navigateToStep(step: AppSteps, substep?: SubStepsType): void {
    this.currentPageIndex.set(step);
    this.setPriviousStepsComplete();
    this.isBrowserNavigation = false;

    this.stepsState.update((state) => {
      const steps = [...state];
      steps[step] = substep ?? this.currentSubStep();
      return steps;
    });

    this.applyStep();
  }

  public setStepCompletion(isCompleted: boolean, step?: AppSteps): void {
    this.steps.update((state) => {
      const steps = [...state];
      steps[step ?? this.currentPageIndex()].isCompleted = isCompleted;
      return steps;
    });
  }

  public isStepLocked(route: string | undefined): boolean {
    if (route === ROUTER_PATHS.SUCCESS_PAGE) {
      return !this.isProcessCompleted();
    }

    const previousStepIndex = this.steps().findIndex((s) => s.route === `/${route}`) - 1;

    if (previousStepIndex < 0) return false;

    return !this.steps()[previousStepIndex].isCompleted;
  }

  public setGlobalLoader(isLoading: boolean): void {
    if (isLoading) this.processTypeStore.setIsLoading(isLoading);
    this.globalLoader.set(isLoading);
  }

  private setNewStep(): void {
    if (this.actionOnSubmit) {
      this.mainStore.setIsLoading(true);
      this.actionOnSubmit().subscribe({
        next: () => {
          this.applyStep(true);
          this.actionOnSubmit = undefined;
          this.isBrowserNavigation = false;
        }
      });
    } else {
      this.applyStep(true);
    }
  }

  private setSubStep(): void {
    this.stepsState.update((state) => {
      const steps = [...state];
      steps[this.currentPageIndex()]++;
      return steps;
    });

    this.applyStep();
  }

  private setPriviousStepsComplete(): void {
    for (let i = 0; i < this.currentPageIndex(); i++) {
      this.setStepCompletion(true, i);
    }
  }

  private applyStep(isNewStep?: boolean): void {
    if (this.useGateway() && this.isGatewayStep(isNewStep)) return this.showGateway();

    const stepMap = {
      [ProgressStepNames.TripDetails]: this.isNewUser ? newUserStepProgressMap : stepProgressMap,
      [ProgressStepNames.CoverageSelection]: coversStepProgressMap,
      [ProgressStepNames.TravelersDetails]: travellerStepProgressMap,
      [ProgressStepNames.SecureCheckout]: checkoutStepProgressMap
    };

    const route = this.steps()[isNewStep ? this.currentPageIndex() + 1 : this.currentPageIndex()].route;
    const fragment = this.getFragment();

    this.navigationService
      .navigate([route], { replaceUrl: !isNewStep, queryParams: { [QUERY_PARAMS.SUB_STEP]: fragment } })
      .pipe(
        tap(() =>
          this.progressStepperService.updateProgress(
            (stepMap[this.currentStep()] as Record<number, number>)[this.currentSubStep()]
          )
        )
      )
      .subscribe(() => {
        this.processTypeStore.setIsLoading(this.globalLoader());
      });
  }

  private getFragment(): string {
    const fragmentMap = {
      [ProgressStepNames.TripDetails]: this.isNewUser ? newUserFragmentsProgressMap : fragmentsProgressMap,
      [ProgressStepNames.CoverageSelection]: coversFragmentsProgressMap,
      [ProgressStepNames.TravelersDetails]: travellerFragmentsProgressMap,
      [ProgressStepNames.SecureCheckout]: checkoutFragmentsProgressMap
    };

    return (fragmentMap[this.currentStep()] as Record<number, string>)[this.currentSubStep()];
  }

  private isGatewayStep(isNewStep?: boolean): boolean {
    const showGatewayNext = this.currentPageIndex() === 0 && this.isStepCompleted();

    if (isNewStep) {
      return showGatewayNext && !this.currentUrl.includes(ROUTER_PATHS.GATEWAY);
    }

    return (
      showGatewayNext &&
      !(this.currentUrl.includes(ROUTER_PATHS.GATEWAY) || this.previousUrl.includes(ROUTER_PATHS.GATEWAY))
    );
  }

  private filteredQueryParams(): Record<string, string> {
    const excludedKeys = [UrlVarsKey.QuoteID, UrlVarsKey.BasicQuoteID, UrlVarsKey.TripDetails];
    const currentParams = this.route.snapshot.queryParams;

    const filteredParams = Object.fromEntries(
      Object.entries(currentParams).filter(([key]) => !excludedKeys.includes(key as UrlVarsKey))
    );

    return filteredParams;
  }
}
